// FILE: src/pages/DetailedReportPage.jsx
//
// Visual redesign sourced from a Claude Design handoff bundle (Ledger.html).
//
// FUNCTIONAL CONTRACT PRESERVED:
//   - URL state for filters + pagination (page, limit, accountId, startDate, endDate)
//   - Default 200 rows per page
//   - Draft + commit pattern — user must click Search to fetch
//   - Race-condition guard via requestIdRef
//   - All summary stats use server-returned values (openingBalance, closingBalance,
//     totalCount, entries) — no client-side recomputation
//   - **CSV EXPORT IS COMPLETELY UNCHANGED** — handleExport is byte-for-byte
//     identical to the previous implementation. Do not modify it.
//
// NEW IN THIS REDESIGN:
//   - Date range defaults to the last 30 days when URL has no startDate/endDate
//   - New page header with account context
//   - Custom Account Picker popover with search
//   - Summary band showing 6 stats: account, period, opening, closing, net change,
//     transactions (with per-day average)
//   - Type chips with colour-coded dots
//   - Tinted opening/closing rows in the table
//   - Skeleton loading state, friendly pre-fetch and empty states
//
// All page styles live in PAGE_CSS, scoped under `.lgrpg-root`.

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../api";
import { formatMoney } from "../utils/formatMoney";

// ────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────
const DETAILED_REPORT_DEFAULT_LIMIT = 200;
const DETAILED_REPORT_ALLOWED_LIMITS = [10, 25, 50, 100, 200];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ────────────────────────────────────────────────────────────────────
// URL <-> draft state helpers
// ────────────────────────────────────────────────────────────────────
function parseDetailedReportParams(searchParams) {
  const rawPage = parseInt(searchParams.get("page"), 10);
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
  const rawLimit = parseInt(searchParams.get("limit"), 10);
  const limit = DETAILED_REPORT_ALLOWED_LIMITS.includes(rawLimit)
    ? rawLimit
    : DETAILED_REPORT_DEFAULT_LIMIT;
  return {
    page,
    limit,
    accountId: searchParams.get("accountId") || "",
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
  };
}

// Last 30 days (today inclusive). Computed once per mount so it stays stable
// for the session.
function computeDefaultDateRange() {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 29);
  const isoYmd = (d) => d.toISOString().split("T")[0];
  return { start: isoYmd(thirtyDaysAgo), end: isoYmd(today) };
}

// ────────────────────────────────────────────────────────────────────
// Formatting helpers
// ────────────────────────────────────────────────────────────────────
function fmtDateMonoFromIso(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}-${MON[+m - 1] || ""}-${y}`;
}

function fmtRangeShort(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const s = new Date(startIso);
  const e = new Date(endIso);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
  const sameYear = s.getFullYear() === e.getFullYear();
  if (sameYear) {
    return (
      <>
        {`${String(s.getDate()).padStart(2, "0")} ${MON[s.getMonth()]}`}
        <span className="lg-stat-arrow" aria-hidden=" "> → </span>
        {`${String(e.getDate()).padStart(2, "0")} ${MON[e.getMonth()]} ${e.getFullYear()}`}
      </>
    );
  }
  return (
    <>
      {fmtDateMonoFromIso(startIso)}
      <span className="lg-stat-arrow" aria-hidden=" "> → </span>
      {fmtDateMonoFromIso(endIso)}
    </>
  );
}

function daysBetween(startIso, endIso) {
  if (!startIso || !endIso) return 1;
  const s = new Date(startIso);
  const e = new Date(endIso);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return 1;
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
}

function formatMoneySigned(value, currency) {
  const v = Number(value) || 0;
  const sign = v > 0 ? "+ " : v < 0 ? "− " : "";
  return sign + formatMoney(Math.abs(v), currency);
}

// Format a transaction date string (from server, ISO) into "DD Mon YYYY"
function fmtTxnDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")} ${MON[d.getMonth()]} ${d.getFullYear()}`;
}

// ────────────────────────────────────────────────────────────────────
// Type chip colours (matches Transactions and Dashboard)
// ────────────────────────────────────────────────────────────────────
const TYPE_COLOURS = {
  Receipt: "#2E7D32",
  Payment: "#C62828",
  Transfer: "#ED6C02",
};

// ────────────────────────────────────────────────────────────────────
// Scoped page CSS (sourced from Ledger.html, prefixed with .lgrpg-root)
// ────────────────────────────────────────────────────────────────────
const PAGE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

.lgrpg-root {
  /* Sticky offsets — the global NavBar is sticky at top:0 with height 60px,
     so anything sticky inside this page sticks BELOW that. --lgrpg-top-h
     is set live by a ResizeObserver in the component so the table thead
     can stick directly under the page-top section. */
  --lgrpg-navbar-h: 60px;
  --lgrpg-top-h: 130px;

  --c-bg: #FAFBFC;
  --c-surface: #FFFFFF;
  --c-surface-sub: #F6F8FA;
  --c-text: #0B0C0E;
  --c-text-2: #4A5060;
  --c-text-3: #6B7280;
  --c-border: #E4E7EC;
  --c-border-2: #EEF1F4;
  --c-border-strong: #D0D5DD;

  --c-primary: #1E90FF;
  --c-primary-2: #1271D6;
  --c-primary-tint: #EBF4FE;
  --c-accent: #FFC107;

  --c-receipt: #2E7D32;
  --c-payment: #C62828;
  --c-transfer: #ED6C02;

  --c-debit: #2E7D32;
  --c-credit: #C62828;
  --c-bal-neg: #C62828;

  --font-sans: 'Geist', 'Söhne', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --font-mono: 'Geist Mono', 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;

  --r-sm: 4px;
  --r-md: 6px;
  --r-lg: 8px;
  --r-xl: 12px;

  --sh-card: 0 0 0 1px var(--c-border);
  --sh-pop: 0 1px 2px rgba(16,24,40,.05), 0 12px 28px rgba(16,24,40,.12);

  font-family: var(--font-sans);
  font-size: 13px;
  color: var(--c-text);
  background: var(--c-bg);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-feature-settings: 'cv11', 'ss03';

  min-height: 100vh;
}
.lgrpg-root *, .lgrpg-root *::before, .lgrpg-root *::after { box-sizing: border-box; }
.lgrpg-root button { font-family: inherit; cursor: pointer; }
.lgrpg-root input, .lgrpg-root select, .lgrpg-root button { font: inherit; color: inherit; }
.lgrpg-root .lg-num { font-variant-numeric: tabular-nums; font-feature-settings: 'tnum' 1; }

/* ────── Page shell ────── */
.lgrpg-root .lg-page {
  background: var(--c-bg);
  display: flex; flex-direction: column;
}
.lgrpg-root .lg-page-top {
  background: var(--c-bg);
  display: flex; flex-direction: column;
  border-bottom: 1px solid var(--c-border);
  position: sticky;
  top: var(--lgrpg-navbar-h);
  z-index: 20;
  box-shadow: 0 1px 0 rgba(16,24,40,0.02), 0 6px 14px -10px rgba(16,24,40,0.08);
}
.lgrpg-root .lg-main {
  max-width: 1440px;
  margin: 0 auto;
  padding: 14px 28px 80px;
  width: 100%;
  display: flex; flex-direction: column; gap: 12px;
}
.lgrpg-root .lg-main-top {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 28px 12px;
  width: 100%;
  display: flex; flex-direction: column; gap: 8px;
}

/* ────── Header ────── */
.lgrpg-root .lg-header {
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
  padding: 16px 28px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}
.lgrpg-root .lg-header-l { display: flex; align-items: center; gap: 14px; min-width: 0; }
.lgrpg-root .lg-header h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.015em;
  line-height: 1.2;
  color: var(--c-text);
}
.lgrpg-root .lg-header-title-wrap {
  display: flex; align-items: baseline; gap: 10px;
  padding-left: 14px;
  border-left: 1px solid var(--c-border);
}
.lgrpg-root .lg-sub {
  margin: 0;
  font-size: 12px;
  color: var(--c-text-3);
  max-width: 560px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ────── Filter bar ────── */
.lgrpg-root .lg-filterbar {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: nowrap;
}
.lgrpg-root .lg-filter-fields {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1 1 0;
  min-width: 0;
  flex-wrap: nowrap;
}
.lgrpg-root .lg-filter-fields > .lg-field + .lg-field {
  border-left: 1px solid var(--c-border-2);
  margin-left: 8px;
  padding-left: 8px;
}
.lgrpg-root .lg-date-sep {
  color: var(--c-text-3);
  font-size: 12px;
  padding: 0 2px;
  margin-left: 8px;
  padding-left: 8px;
  border-left: 1px solid var(--c-border-2);
}
.lgrpg-root .lg-date-sep + .lg-field {
  border-left: 0 !important;
  margin-left: 4px !important;
  padding-left: 0 !important;
}
.lgrpg-root .lg-filter-actions {
  display: flex; align-items: center; gap: 6px;
  flex-shrink: 0;
}

.lgrpg-root .lg-field { display: flex; align-items: center; gap: 6px; position: relative; min-width: 0; }
.lgrpg-root .lg-field-account { flex: 1 1 200px; min-width: 140px; max-width: 380px; }
.lgrpg-root .lg-field-date { flex: 0 0 auto; }

.lgrpg-root .lg-input {
  height: 32px;
  display: flex; align-items: center;
  gap: 8px;
  padding: 0 10px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-md);
  font-size: 12.5px;
  color: var(--c-text);
  cursor: text;
  transition: border-color .12s, box-shadow .12s;
}
.lgrpg-root .lg-input:hover { border-color: var(--c-border-strong); }
.lgrpg-root .lg-input:focus-within,
.lgrpg-root .lg-input.open {
  border-color: var(--c-primary);
  box-shadow: 0 0 0 3px rgba(30,144,255,.16);
  outline: none;
}
.lgrpg-root .lg-input-select {
  cursor: default;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  appearance: none;
  width: 100%;
  min-width: 0;
  text-align: left;
  justify-content: space-between;
  padding-right: 8px;
}
.lgrpg-root .lg-input-select.placeholder { color: var(--c-text-3); }
.lgrpg-root .lg-input-date {
  position: relative;
  color: var(--c-text-3);
}
.lgrpg-root .lg-input-date svg { flex-shrink: 0; }
.lgrpg-root .lg-input-date input {
  border: 0; outline: 0; background: transparent;
  width: 110px;
  font-size: 12.5px;
  color: var(--c-text);
  font-variant-numeric: tabular-nums;
  font-family: var(--font-mono);
}
.lgrpg-root .lg-input-date input::-webkit-calendar-picker-indicator {
  opacity: 0.55; cursor: pointer; padding: 0; margin: 0;
}

.lgrpg-root .lg-acc-pill {
  display: inline-flex; align-items: baseline; gap: 7px;
  font-size: 12.5px; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.lgrpg-root .lg-acc-mark {
  width: 6px; height: 6px; border-radius: 999px;
  background: var(--c-primary);
  align-self: center;
  flex-shrink: 0;
  box-shadow: 0 0 0 3px var(--c-primary-tint);
}
.lgrpg-root .lg-acc-name { font-weight: 600; flex-shrink: 0; }
.lgrpg-root .lg-acc-num {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--c-text-3);
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.lgrpg-root .lg-acc-placeholder { color: var(--c-text-3); }
.lgrpg-root .lg-caret { color: var(--c-text-3); flex-shrink: 0; margin-left: 8px; transition: transform .15s; }
.lgrpg-root .lg-input-select.open .lg-caret { transform: rotate(180deg); }

/* Popover */
.lgrpg-root .lg-pop {
  position: absolute;
  top: calc(100% + 6px); left: 0;
  width: 100%; min-width: 340px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-pop);
  z-index: 30;
  display: flex; flex-direction: column;
  overflow: hidden;
}
.lgrpg-root .lg-pop-search {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--c-border-2);
  color: var(--c-text-3);
}
.lgrpg-root .lg-pop-search input {
  flex: 1; border: 0; outline: 0; background: transparent;
  font-size: 13px; color: var(--c-text);
}
.lgrpg-root .lg-pop-search input::placeholder { color: var(--c-text-3); }
.lgrpg-root .lg-pop-clear {
  border: 0; background: transparent; cursor: pointer;
  width: 18px; height: 18px; line-height: 1;
  font-size: 14px; color: var(--c-text-3);
  border-radius: 4px;
}
.lgrpg-root .lg-pop-clear:hover { background: var(--c-surface-sub); color: var(--c-text); }
.lgrpg-root .lg-pop-list { max-height: 320px; overflow-y: auto; padding: 4px; }
.lgrpg-root .lg-pop-item {
  width: 100%;
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 10px; gap: 10px;
  border: 0; background: transparent;
  text-align: left; cursor: pointer;
  border-radius: var(--r-md);
}
.lgrpg-root .lg-pop-item:hover { background: var(--c-surface-sub); }
.lgrpg-root .lg-pop-item.is-sel { background: var(--c-primary-tint); }
.lgrpg-root .lg-pop-item-name { font-size: 13px; font-weight: 500; color: var(--c-text); }
.lgrpg-root .lg-pop-item-meta { font-family: var(--font-mono); font-size: 11px; color: var(--c-text-3); margin-top: 2px; }
.lgrpg-root .lg-pop-empty { padding: 16px 12px; color: var(--c-text-3); font-size: 12.5px; }
.lgrpg-root .lg-pop-foot { padding: 8px 12px; border-top: 1px solid var(--c-border-2); font-size: 11px; color: var(--c-text-3); }

/* Buttons */
.lgrpg-root .lg-btn {
  height: 32px;
  padding: 0 12px;
  display: inline-flex; align-items: center; gap: 6px;
  border-radius: var(--r-md);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background .12s, border-color .12s, color .12s, box-shadow .12s;
  white-space: nowrap;
  background: transparent;
  color: inherit;
}
.lgrpg-root .lg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.lgrpg-root .lg-btn-primary {
  background: var(--c-primary);
  color: white;
  border-color: var(--c-primary);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.18), 0 1px 2px rgba(13,82,154,.18);
}
.lgrpg-root .lg-btn-primary:hover:not(:disabled) { background: var(--c-primary-2); border-color: var(--c-primary-2); }
.lgrpg-root .lg-btn-secondary {
  background: var(--c-surface);
  color: var(--c-text);
  border-color: var(--c-border-strong);
}
.lgrpg-root .lg-btn-secondary:hover:not(:disabled) { background: var(--c-surface-sub); border-color: var(--c-text-3); }
.lgrpg-root .lg-btn-ghost { background: transparent; color: var(--c-text-3); }
.lgrpg-root .lg-btn-ghost:hover { color: var(--c-text); background: var(--c-surface-sub); }

.lgrpg-root .lg-spinner {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid currentColor; border-right-color: transparent;
  animation: lgrpg-spin .9s linear infinite;
  display: inline-block; vertical-align: -2px;
  opacity: .75;
}
.lgrpg-root .lg-spinner-sm { width: 11px; height: 11px; border-width: 1.5px; }
@keyframes lgrpg-spin { to { transform: rotate(360deg); } }

/* ────── Summary band ────── */
.lgrpg-root .lg-summary {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  padding: 10px 14px;
  display: flex;
  align-items: stretch;
  gap: 0;
  flex-wrap: nowrap;
  overflow: hidden;
}
.lgrpg-root .lg-stat-divider {
  width: 1px;
  background: var(--c-border-2);
  margin: 4px 10px;
  flex-shrink: 0;
}
.lgrpg-root .lg-stat {
  display: flex; flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1 1 0;
  justify-content: center;
}
.lgrpg-root .lg-stat-account { flex: 1.2 1 0; min-width: 140px; }
.lgrpg-root .lg-stat-period { flex: 1.2 1 0; }
.lgrpg-root .lg-stat-net { flex: 1.05 1 0; }
.lgrpg-root .lg-stat-sub-inline {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  color: var(--c-text-3);
  font-variant-numeric: tabular-nums;
}
.lgrpg-root .lg-stat-lab {
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--c-text-3);
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lgrpg-root .lg-stat-val {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: var(--c-text);
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lgrpg-root .lg-stat-mono {
  font-family: var(--font-mono);
  font-size: 12.5px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--c-text);
}
.lgrpg-root .lg-stat-arrow { color: var(--c-text-3); padding: 0 2px; }
.lgrpg-root .lg-stat-account-val {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.lgrpg-root .lg-stat-account-name {
  font-weight: 600;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex-shrink: 1;
}

/* ────── Chip ────── */
.lgrpg-root .lg-chip {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--c-text);
}
.lgrpg-root .lg-chip-dot {
  width: 7px; height: 7px;
  border-radius: 999px;
  box-shadow: 0 0 0 2px rgba(0,0,0,0.04);
  flex-shrink: 0;
}

/* ────── Table ────── */
.lgrpg-root .lg-table-wrap {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  position: relative;
  /* NOTE: do NOT set overflow here. An overflow value (auto/scroll/hidden)
     creates a new scrolling container, which would change the anchor for the
     sticky <th> cells and make the column headers render in the middle of
     the table body. If a wide table needs horizontal scrolling on small
     viewports, that scroll should happen at the page/body level. */
}
.lgrpg-root .lg-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  table-layout: fixed;
}
.lgrpg-root .lg-table col.c-date { width: 92px; }
.lgrpg-root .lg-table col.c-type { width: 92px; }
.lgrpg-root .lg-table col.c-cat { width: 156px; }
.lgrpg-root .lg-table col.c-desc { width: auto; }
.lgrpg-root .lg-table col.c-debit { width: 104px; }
.lgrpg-root .lg-table col.c-credit { width: 104px; }
.lgrpg-root .lg-table col.c-bal { width: 132px; }

.lgrpg-root .lg-th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--c-text-2);
  padding: 14px 12px;
  background: var(--c-surface-sub);
  border-bottom: 1px solid var(--c-border);
  white-space: nowrap;
  vertical-align: middle;
  /* Sticky beneath the page-top section. --lgrpg-top-h is updated by the
     ResizeObserver in the component when the page-top resizes (summary
     band appears/disappears, viewport reflows, etc). */
  position: sticky;
  top: calc(var(--lgrpg-navbar-h) + var(--lgrpg-top-h));
  z-index: 5;
}
.lgrpg-root .lg-th-money { text-align: right; }
.lgrpg-root .lg-th-bal { padding-right: 18px; }

.lgrpg-root .lg-row { transition: background .08s; }
.lgrpg-root .lg-row:hover .lg-td { background: #FBFCFD; }
.lgrpg-root .lg-td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--c-border-2);
  color: var(--c-text);
  vertical-align: top;
}
.lgrpg-root .lg-row:last-child .lg-td { border-bottom: 0; }
.lgrpg-root .lg-td-date {
  font-family: var(--font-mono);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--c-text-2);
  white-space: nowrap;
}
.lgrpg-root .lg-td-cat {
  color: var(--c-text-2);
  font-size: 12.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lgrpg-root .lg-td-desc-cell {
  position: relative;
}
.lgrpg-root .lg-desc-inner {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.lgrpg-root .lg-td-desc {
  color: var(--c-text);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.4;
}
.lgrpg-root .lg-td-desc-cell.is-expanded .lg-desc-inner { flex-wrap: wrap; }
.lgrpg-root .lg-td-desc-cell.is-expanded .lg-td-desc {
  white-space: normal;
  text-overflow: clip;
  overflow: visible;
  flex: 1 1 100%;
}
.lgrpg-root .lg-desc-more {
  display: inline-block;
  border: 0;
  background: transparent;
  color: var(--c-primary);
  font-size: 11.5px;
  font-weight: 500;
  cursor: pointer;
  padding: 1px 4px;
  border-radius: 3px;
  flex-shrink: 0;
}
.lgrpg-root .lg-desc-more:hover { color: var(--c-primary-2); text-decoration: underline; }
.lgrpg-root .lg-td-debit, .lgrpg-root .lg-td-credit, .lgrpg-root .lg-td-balance {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-family: var(--font-mono);
  font-size: 12.5px;
  white-space: nowrap;
}
.lgrpg-root .lg-td-debit { color: var(--c-debit); font-weight: 500; }
.lgrpg-root .lg-td-credit { color: var(--c-credit); font-weight: 500; }
.lgrpg-root .lg-td-balance { color: var(--c-text); padding-right: 18px; }
.lgrpg-root .lg-td-balance.is-neg { color: var(--c-bal-neg); }
.lgrpg-root .is-empty { color: var(--c-border-strong); font-weight: 400; }
.lgrpg-root .lg-num-bal { font-size: 13.5px; font-weight: 700; }

/* Opening / closing rows */
.lgrpg-root .lg-row-oc .lg-td { background: #F8FAFC; }
.lgrpg-root .lg-row-oc:hover .lg-td { background: #F4F7FA; }
.lgrpg-root .lg-oc-label {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--c-text-2);
}
.lgrpg-root .lg-oc-mark { color: var(--c-primary); font-size: 11px; }
.lgrpg-root .lg-oc-date {
  font-family: var(--font-mono);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--c-text-2);
  white-space: nowrap;
}
.lgrpg-root .lg-row-open .lg-td { box-shadow: inset 0 2px 0 var(--c-primary); }
.lgrpg-root .lg-row-close .lg-td { box-shadow: inset 0 -2px 0 var(--c-primary); border-bottom: 0; }

/* ────── Pagination ────── */
.lgrpg-root .lg-pag {
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px;
  padding: 10px 4px 0;
  flex-wrap: wrap;
}
.lgrpg-root .lg-pag-info { font-size: 12.5px; color: var(--c-text-3); }
.lgrpg-root .lg-pag-info b { color: var(--c-text); font-weight: 600; font-variant-numeric: tabular-nums; }
.lgrpg-root .lg-pag-right { display: flex; align-items: center; gap: 18px; }
.lgrpg-root .lg-pag-per { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--c-text-3); }
.lgrpg-root .lg-pag-per select {
  height: 30px;
  border: 1px solid var(--c-border);
  border-radius: var(--r-md);
  padding: 0 24px 0 10px;
  background: var(--c-surface);
  font-size: 12.5px;
  color: var(--c-text);
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'><path d='M2 4l3 3 3-3' fill='none' stroke='%236B7280' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 8px center;
  cursor: pointer;
}
.lgrpg-root .lg-pag-nav { display: inline-flex; align-items: center; gap: 4px; }
.lgrpg-root .lg-pag-arr, .lgrpg-root .lg-pag-page {
  min-width: 30px; height: 30px; padding: 0 8px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid var(--c-border);
  background: var(--c-surface);
  border-radius: var(--r-md);
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  color: var(--c-text-2);
  cursor: pointer;
}
.lgrpg-root .lg-pag-arr:hover:not(:disabled),
.lgrpg-root .lg-pag-page:hover { background: var(--c-surface-sub); color: var(--c-text); }
.lgrpg-root .lg-pag-arr:disabled { opacity: .4; cursor: not-allowed; }
.lgrpg-root .lg-pag-page.is-active {
  background: var(--c-primary);
  border-color: var(--c-primary);
  color: white;
  font-weight: 600;
}
.lgrpg-root .lg-pag-ellipsis { color: var(--c-text-3); padding: 0 2px; font-size: 12px; }

/* ────── States ────── */
.lgrpg-root .lg-state {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  padding: 56px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
}
.lgrpg-root .lg-state-prefetch { padding: 72px 28px; }
.lgrpg-root .lg-state-art { margin-bottom: 8px; }
.lgrpg-root .lg-state h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--c-text);
}
.lgrpg-root .lg-state p {
  margin: 0;
  font-size: 13px;
  color: var(--c-text-3);
  max-width: 460px;
  line-height: 1.55;
}
.lgrpg-root .lg-state-hint {
  margin-top: 12px;
  font-size: 11.5px;
  color: var(--c-text-3);
}
.lgrpg-root .lg-state-hint kbd {
  font-family: var(--font-mono);
  font-size: 10.5px;
  background: var(--c-surface-sub);
  border: 1px solid var(--c-border);
  padding: 1px 6px;
  border-radius: 4px;
  color: var(--c-text-2);
}

/* Loading skeleton */
.lgrpg-root .lg-state-loading { padding: 0; align-items: stretch; background: transparent; border: 0; gap: 20px; }
.lgrpg-root .lg-skel-summary {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  padding: 22px;
  display: flex; flex-direction: column; gap: 14px;
}
.lgrpg-root .lg-skel-row { display: flex; gap: 32px; }
.lgrpg-root .lg-skel-table { padding: 0; background: var(--c-surface); border: 1px solid var(--c-border); border-radius: var(--r-lg); overflow: hidden; }
.lgrpg-root .lg-skel-thead {
  display: grid; grid-template-columns: 92px 92px 156px 1fr 104px 104px 132px;
  gap: 0;
  padding: 12px;
  background: var(--c-surface-sub);
  border-bottom: 1px solid var(--c-border);
}
.lgrpg-root .lg-skel-thead .lg-skel-block { height: 10px; }
.lgrpg-root .lg-skel-row-tr {
  display: grid; grid-template-columns: 92px 92px 156px 1fr 104px 104px 132px;
  gap: 0;
  padding: 14px 12px;
  border-bottom: 1px solid var(--c-border-2);
  align-items: center;
}
.lgrpg-root .lg-skel-block {
  height: 12px; border-radius: 3px;
  background: linear-gradient(90deg, #EEF1F4 0%, #F6F8FA 50%, #EEF1F4 100%);
  background-size: 200% 100%;
  animation: lgrpg-shimmer 1.4s infinite;
}
.lgrpg-root .lg-skel-r { justify-self: end; margin-right: 8px; }
@keyframes lgrpg-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.lgrpg-root .lg-skel-foot {
  display: flex; align-items: center; gap: 10px;
  color: var(--c-text-3);
  font-size: 12.5px;
  justify-content: center;
  padding: 8px;
}
.lgrpg-root .lg-skel-foot .lg-spinner { color: var(--c-primary); opacity: 1; }

/* Error state */
.lgrpg-root .lg-state-error {
  flex-direction: row;
  text-align: left;
  align-items: flex-start;
  background: #FFF6F6;
  border-color: #F3D2D2;
  padding: 20px 22px;
  gap: 16px;
}
.lgrpg-root .lg-error-icon { flex-shrink: 0; padding-top: 2px; }
.lgrpg-root .lg-error-text { flex: 1; }
.lgrpg-root .lg-error-title { font-size: 14px; font-weight: 600; color: #8C1717; margin-bottom: 4px; }
.lgrpg-root .lg-error-body { font-size: 13px; color: #6A1D1D; line-height: 1.5; }

@media (max-width: 760px) {
  .lgrpg-root .lg-main { padding: 0 14px 64px; }
  .lgrpg-root .lg-header { padding: 12px 14px 10px; }
  .lgrpg-root .lg-header-title-wrap .lg-sub { display: none; }
  .lgrpg-root .lg-filter-fields { width: 100%; }
}
`;

// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────

function AccountPicker({ account, accounts, onChange, busy }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    if (!query) return accounts;
    const q = query.toLowerCase();
    return accounts.filter(
      (a) =>
        (a.accountName || "").toLowerCase().includes(q) ||
        (a.accountNo || "").toLowerCase().includes(q) ||
        (a.description || "").toLowerCase().includes(q)
    );
  }, [accounts, query]);

  return (
    <div className="lg-field lg-field-account" ref={wrapRef}>
      <button
        type="button"
        className={`lg-input lg-input-select ${account ? "has-value" : "placeholder"} ${open ? "open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
      >
        {account ? (
          <span className="lg-acc-pill">
            <span className="lg-acc-mark" />
            <span className="lg-acc-name">{account.accountName}</span>
            <span className="lg-acc-num">· #{account.accountNo}</span>
          </span>
        ) : (
          <span className="lg-acc-placeholder">Select an account…</span>
        )}
        <svg width="12" height="12" viewBox="0 0 12 12" className="lg-caret" aria-hidden=" ">
          <path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="lg-pop">
          <div className="lg-pop-search">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden=" ">
              <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
              <path d="M10.5 10.5l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <input
              autoFocus
              placeholder="Search accounts"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button className="lg-pop-clear" onClick={() => setQuery("")}>
                ×
              </button>
            )}
          </div>
          <div className="lg-pop-list" role="listbox">
            {filtered.length === 0 && (
              <div className="lg-pop-empty">No accounts match "{query}".</div>
            )}
            {filtered.map((a) => (
              <button
                key={a._id}
                role="option"
                aria-selected={account && a._id === account._id}
                className={`lg-pop-item ${account && a._id === account._id ? "is-sel" : ""}`}
                onClick={() => {
                  onChange(a);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <div>
                  <div className="lg-pop-item-name">{a.accountName}</div>
                  <div className="lg-pop-item-meta">#{a.accountNo}</div>
                </div>
                {account && a._id === account._id && (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden=" ">
                    <path d="M3.5 8.5l3 3 6-7" stroke="#1E90FF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            ))}
          </div>
          <div className="lg-pop-foot">{accounts.length} accounts you can view</div>
        </div>
      )}
    </div>
  );
}

function DateField({ label, value, onChange, disabled }) {
  return (
    <div className="lg-field lg-field-date">
      <div className="lg-input lg-input-date" title={label}>
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden=" ">
          <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.25" />
          <path d="M2.5 6.5h11M5.5 2v3M10.5 2v3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
        </svg>
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function FilterBar({
  draftAccount,
  setDraftAccount,
  accounts,
  draftStart,
  setDraftStart,
  draftEnd,
  setDraftEnd,
  onSearch,
  onExport,
  onClear,
  hasFilters,
  exporting,
  busy,
}) {
  function onKey(e) {
    if (e.key === "Enter") onSearch();
  }
  return (
    <section className="lg-filterbar" onKeyDown={onKey} role="search" aria-label="Filter ledger">
      <div className="lg-filter-fields">
        <AccountPicker
          account={draftAccount}
          accounts={accounts}
          onChange={setDraftAccount}
          busy={busy}
        />
        <DateField label="Start date" value={draftStart} onChange={setDraftStart} disabled={busy} />
        <span className="lg-date-sep" aria-hidden=" ">→</span>
        <DateField label="End date" value={draftEnd} onChange={setDraftEnd} disabled={busy} />
      </div>
      <div className="lg-filter-actions">
        {hasFilters && (
          <button type="button" className="lg-btn lg-btn-ghost" onClick={onClear}>
            Clear
          </button>
        )}
        <button
          type="button"
          className="lg-btn lg-btn-secondary"
          onClick={onExport}
          disabled={exporting || busy || !draftAccount}
        >
          {exporting ? (
            <>
              <span className="lg-spinner lg-spinner-sm" aria-hidden=" " />
              <span>Preparing…</span>
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden=" ">
                <path d="M3 11v2.5h10V11M8 2.5v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Export CSV</span>
            </>
          )}
        </button>
        <button
          type="button"
          className="lg-btn lg-btn-primary"
          onClick={onSearch}
          disabled={busy || !draftAccount}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden=" ">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.5 10.5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span>Search</span>
        </button>
      </div>
    </section>
  );
}

function SummaryBand({ account, start, end, opening, closing, txCount, currency }) {
  const net = (closing || 0) - (opening || 0);
  const netColor = net > 0 ? "#2E7D32" : net < 0 ? "#C62828" : "#666666";
  const days = daysBetween(start, end);
  const avg = (txCount || 0) / days;
  const range = fmtRangeShort(start, end);

  return (
    <section className="lg-summary" aria-label="Account summary">
      <div className="lg-stat lg-stat-account">
        <div className="lg-stat-lab">Account</div>
        <div className="lg-stat-val lg-stat-account-val">
          <span className="lg-acc-mark" aria-hidden=" " />
          <span className="lg-stat-account-name">{account?.accountName || "—"}</span>
        </div>
      </div>
      <div className="lg-stat-divider" />
      <div className="lg-stat lg-stat-period">
        <div className="lg-stat-lab">Period · {days} {days === 1 ? "day" : "days"}</div>
        <div className="lg-stat-val lg-stat-mono">{range || "—"}</div>
      </div>
      <div className="lg-stat-divider" />
      <div className="lg-stat">
        <div className="lg-stat-lab">Opening</div>
        <div className="lg-stat-val lg-num">{formatMoney(opening || 0, currency)}</div>
      </div>
      <div className="lg-stat-divider" />
      <div className="lg-stat">
        <div className="lg-stat-lab">Closing</div>
        <div className="lg-stat-val lg-num">{formatMoney(closing || 0, currency)}</div>
      </div>
      <div className="lg-stat-divider" />
      <div className="lg-stat lg-stat-net">
        <div className="lg-stat-lab">Net change</div>
        <div className="lg-stat-val lg-num" style={{ color: netColor }}>
          {formatMoneySigned(net, currency)}
        </div>
      </div>
      <div className="lg-stat-divider" />
      <div className="lg-stat">
        <div className="lg-stat-lab">Transactions</div>
        <div className="lg-stat-val">
          <span className="lg-num">{(txCount || 0).toLocaleString("en-GB")}</span>
          <span className="lg-stat-sub-inline"> · {avg.toFixed(1)}/day</span>
        </div>
      </div>
    </section>
  );
}

function TypeChip({ type }) {
  return (
    <span className="lg-chip">
      <span className="lg-chip-dot" style={{ background: TYPE_COLOURS[type] || "#6B7280" }} />
      <span>{type}</span>
    </span>
  );
}

function DescriptionCell({ text }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setTruncated(el.scrollWidth - el.clientWidth > 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);

  return (
    <td className={`lg-td lg-td-desc-cell ${expanded ? "is-expanded" : ""}`}>
      <div className="lg-desc-inner">
        <span ref={ref} className="lg-td-desc" title={truncated && !expanded ? text : undefined}>
          {text || ""}
        </span>
        {truncated && (
          <button
            type="button"
            className="lg-desc-more"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Collapse description" : "Show full description"}
          >
            {expanded ? "less" : "more"}
          </button>
        )}
      </div>
    </td>
  );
}

function LedgerTable({ entries, opening, closing, startIso, endIso, currency }) {
  return (
    <div className="lg-table-wrap" role="region" aria-label="Transaction list" tabIndex={0}>
      <table className="lg-table">
        <colgroup>
          <col className="c-date" />
          <col className="c-type" />
          <col className="c-cat" />
          <col className="c-desc" />
          <col className="c-debit" />
          <col className="c-credit" />
          <col className="c-bal" />
        </colgroup>
        <thead>
          <tr>
            <th className="lg-th">Date</th>
            <th className="lg-th">Type</th>
            <th className="lg-th">Category</th>
            <th className="lg-th">Description</th>
            <th className="lg-th lg-th-money">Debit</th>
            <th className="lg-th lg-th-money">Credit</th>
            <th className="lg-th lg-th-money lg-th-bal">Running balance</th>
          </tr>
        </thead>
        <tbody>
          {/* Opening row (synthetic — always shown when we have data) */}
          <tr className="lg-row lg-row-oc lg-row-open">
            <td className="lg-td">
              <span className="lg-oc-date">{fmtDateMonoFromIso(startIso) || "—"}</span>
            </td>
            <td className="lg-td" colSpan={3}>
              <span className="lg-oc-label">
                <span className="lg-oc-mark" aria-hidden=" ">▸</span>
                Opening balance
              </span>
            </td>
            <td className="lg-td lg-td-debit is-empty">—</td>
            <td className="lg-td lg-td-credit is-empty">—</td>
            <td className="lg-td lg-td-balance">
              <span className="lg-num lg-num-bal">{formatMoney(opening || 0, currency)}</span>
            </td>
          </tr>

          {entries.map((line) => {
            // Skip the server's opening row — we render our own above.
            if (line.isOpeningRow) return null;

            const dateStr = line.date ? fmtTxnDate(line.date) : "";
            const balanceNeg = (line.runningBalance || 0) < 0;

            return (
              <tr key={line._id} className="lg-row">
                <td className="lg-td lg-td-date">{dateStr}</td>
                <td className="lg-td">
                  {line.transactionType ? <TypeChip type={line.transactionType} /> : <span className="is-empty">—</span>}
                </td>
                <td className="lg-td lg-td-cat">{line.categoryName || "—"}</td>
                <DescriptionCell text={line.description} />
                <td className={`lg-td lg-td-debit ${!line.debit ? "is-empty" : ""}`}>
                  {line.debit ? formatMoney(line.debit, currency) : "—"}
                </td>
                <td className={`lg-td lg-td-credit ${!line.credit ? "is-empty" : ""}`}>
                  {line.credit ? formatMoney(line.credit, currency) : "—"}
                </td>
                <td className={`lg-td lg-td-balance ${balanceNeg ? "is-neg" : ""}`}>
                  <span className="lg-num lg-num-bal">{formatMoney(line.runningBalance || 0, currency)}</span>
                </td>
              </tr>
            );
          })}

          {/* Closing row (synthetic) */}
          <tr className="lg-row lg-row-oc lg-row-close">
            <td className="lg-td">
              <span className="lg-oc-date">{fmtDateMonoFromIso(endIso) || "—"}</span>
            </td>
            <td className="lg-td" colSpan={3}>
              <span className="lg-oc-label">
                <span className="lg-oc-mark" aria-hidden=" ">◂</span>
                Closing balance
              </span>
            </td>
            <td className="lg-td lg-td-debit is-empty">—</td>
            <td className="lg-td lg-td-credit is-empty">—</td>
            <td className="lg-td lg-td-balance">
              <span className="lg-num lg-num-bal">{formatMoney(closing || 0, currency)}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Pagination({ page, perPage, total, onPage, onPer }) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(total, page * perPage);

  const seen = new Set();
  const out = [];
  function add(v) {
    if (v < 1 || v > totalPages || seen.has(v)) return;
    seen.add(v);
    out.push(v);
  }
  add(1);
  add(2);
  add(page - 1);
  add(page);
  add(page + 1);
  add(totalPages - 1);
  add(totalPages);
  const sorted = [...seen].sort((a, b) => a - b);
  const display = [];
  for (let i = 0; i < sorted.length; i++) {
    display.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) display.push("…");
  }

  return (
    <section className="lg-pag">
      <div className="lg-pag-info">
        Showing <b>{from.toLocaleString("en-GB")}</b>–<b>{to.toLocaleString("en-GB")}</b> of <b>{total.toLocaleString("en-GB")}</b> rows
      </div>
      <div className="lg-pag-right">
        <label className="lg-pag-per">
          <span>Rows per page</span>
          <select value={perPage} onChange={(e) => onPer(Number(e.target.value))}>
            {DETAILED_REPORT_ALLOWED_LIMITS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        {/* Container is a <div>, not <nav>, because the global styles.css has a
            very loud `nav { background: navy; ... }` rule that would paint the
            entire pagination cluster blue. role + aria-label preserve the
            semantic meaning for screen readers. */}
        <div className="lg-pag-nav" role="navigation" aria-label="Pagination">
          <button
            className="lg-pag-arr"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
            aria-label="Previous page"
          >
            <svg width="11" height="11" viewBox="0 0 12 12">
              <path d="M7.5 2.5L4 6l3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {display.map((p, i) =>
            p === "…" ? (
              <span key={`e${i}`} className="lg-pag-ellipsis">…</span>
            ) : (
              <button
                key={p}
                className={`lg-pag-page ${p === page ? "is-active" : ""}`}
                onClick={() => onPage(p)}
                aria-current={p === page ? "page" : undefined}
              >
                {p}
              </button>
            )
          )}
          <button
            className="lg-pag-arr"
            disabled={page >= totalPages}
            onClick={() => onPage(page + 1)}
            aria-label="Next page"
          >
            <svg width="11" height="11" viewBox="0 0 12 12">
              <path d="M4.5 2.5L8 6l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}

function PreFetchState() {
  return (
    <section className="lg-state lg-state-prefetch">
      <div className="lg-state-art">
        <svg viewBox="0 0 160 120" width="160" height="120" aria-hidden=" ">
          <rect x="20" y="22" width="120" height="80" rx="6" fill="#FAFBFC" stroke="#E4E7EC" strokeWidth="1.5" />
          <rect x="20" y="22" width="120" height="14" rx="6" fill="#F2F4F7" />
          <rect x="26" y="42" width="38" height="4" rx="2" fill="#E4E7EC" />
          <rect x="68" y="42" width="20" height="4" rx="2" fill="#E4E7EC" />
          <rect x="92" y="42" width="42" height="4" rx="2" fill="#E4E7EC" />
          <rect x="26" y="54" width="34" height="3" rx="1.5" fill="#EEF1F4" />
          <rect x="68" y="54" width="24" height="3" rx="1.5" fill="#EEF1F4" />
          <rect x="96" y="54" width="38" height="3" rx="1.5" fill="#EEF1F4" />
          <rect x="26" y="62" width="34" height="3" rx="1.5" fill="#EEF1F4" />
          <rect x="68" y="62" width="24" height="3" rx="1.5" fill="#EEF1F4" />
          <rect x="96" y="62" width="38" height="3" rx="1.5" fill="#EEF1F4" />
          <rect x="26" y="70" width="34" height="3" rx="1.5" fill="#EEF1F4" />
          <rect x="68" y="70" width="24" height="3" rx="1.5" fill="#EEF1F4" />
          <rect x="96" y="70" width="38" height="3" rx="1.5" fill="#EEF1F4" />
          <circle cx="118" cy="14" r="11" fill="#1E90FF" />
          <path d="M118 19v-9M114 14l4-4 4 4" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      </div>
      <h3>Pick an account to view its ledger</h3>
      <p>Select an account from the filter bar above, then click <b>Search</b> to load every transaction in your chosen date range, with running balance.</p>
      <div className="lg-state-hint">
        <kbd>Enter</kbd> from any filter field also runs the search.
      </div>
    </section>
  );
}

function LoadingState() {
  const rowCount = 12;
  return (
    <section className="lg-state lg-state-loading" aria-live="polite" aria-busy="true">
      <div className="lg-skel-summary">
        <div className="lg-skel-block" style={{ width: 280, height: 22 }} />
        <div className="lg-skel-row">
          <div className="lg-skel-block" style={{ width: 96, height: 12 }} />
          <div className="lg-skel-block" style={{ width: 96, height: 12 }} />
          <div className="lg-skel-block" style={{ width: 96, height: 12 }} />
          <div className="lg-skel-block" style={{ width: 96, height: 12 }} />
        </div>
      </div>
      <div className="lg-table-wrap lg-skel-table">
        <div className="lg-skel-thead">
          <div className="lg-skel-block" style={{ width: 60 }} />
          <div className="lg-skel-block" style={{ width: 50 }} />
          <div className="lg-skel-block" style={{ width: 110 }} />
          <div className="lg-skel-block" style={{ width: 180 }} />
          <div className="lg-skel-block lg-skel-r" style={{ width: 60 }} />
          <div className="lg-skel-block lg-skel-r" style={{ width: 60 }} />
          <div className="lg-skel-block lg-skel-r" style={{ width: 80 }} />
        </div>
        {[...Array(rowCount)].map((_, i) => (
          <div key={i} className="lg-skel-row-tr">
            <div className="lg-skel-block" style={{ width: 78 }} />
            <div className="lg-skel-block" style={{ width: 70, height: 18, borderRadius: 9 }} />
            <div className="lg-skel-block" style={{ width: 110 + (i % 3) * 24 }} />
            <div className="lg-skel-block" style={{ width: 180 + (i * 9) % 60 }} />
            <div className="lg-skel-block lg-skel-r" style={{ width: i % 2 === 0 ? 70 : 38 }} />
            <div className="lg-skel-block lg-skel-r" style={{ width: i % 2 === 0 ? 38 : 90 }} />
            <div className="lg-skel-block lg-skel-r" style={{ width: 96 }} />
          </div>
        ))}
      </div>
      <div className="lg-skel-foot">
        <span className="lg-spinner" />
        <span>Loading ledger…</span>
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="lg-state lg-state-empty">
      <div className="lg-state-art">
        <svg viewBox="0 0 160 120" width="160" height="120" aria-hidden=" ">
          <rect x="20" y="22" width="120" height="80" rx="6" fill="#FAFBFC" stroke="#E4E7EC" strokeWidth="1.5" />
          <rect x="20" y="22" width="120" height="14" rx="6" fill="#F2F4F7" />
          <line x1="36" y1="62" x2="124" y2="62" stroke="#E4E7EC" strokeWidth="1.5" strokeDasharray="3 3" />
          <line x1="36" y1="76" x2="124" y2="76" stroke="#E4E7EC" strokeWidth="1.5" strokeDasharray="3 3" />
          <line x1="36" y1="90" x2="124" y2="90" stroke="#E4E7EC" strokeWidth="1.5" strokeDasharray="3 3" />
          <circle cx="80" cy="70" r="14" fill="white" stroke="#FFC107" strokeWidth="2" />
          <path d="M80 65v6M80 75v.5" stroke="#FFC107" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      <h3>No transactions in this date range</h3>
      <p>This account had no movement between the dates you picked. Try widening the date range or selecting a different account.</p>
    </section>
  );
}

function ErrorState({ onRetry, message }) {
  return (
    <section className="lg-state lg-state-error" role="alert">
      <div className="lg-error-icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden=" ">
          <circle cx="12" cy="12" r="9.5" stroke="#C62828" strokeWidth="1.6" />
          <path d="M12 7v6M12 16.5v.5" stroke="#C62828" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
      <div className="lg-error-text">
        <div className="lg-error-title">Couldn't load this ledger</div>
        <div className="lg-error-body">
          {message || "We hit an error while loading. Your filters are still selected — try again, or pick a smaller date range."}
        </div>
      </div>
      <button className="lg-btn lg-btn-secondary" onClick={onRetry}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden=" ">
          <path d="M3.5 8a4.5 4.5 0 1 1 1.3 3.2M3.5 12V8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Try again
      </button>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────
function DetailedReportPage() {
  // URL is the source of truth for committed filters + pagination.
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, limit, accountId, startDate, endDate } = parseDetailedReportParams(searchParams);

  // Compute defaults once per mount (last 30 days), used when URL has no dates.
  const [defaults] = useState(() => computeDefaultDateRange());

  // Draft form state — what the user is editing before clicking Search.
  // Initialised from URL, falling back to the last-30-days default for dates.
  const [draftAccountId, setDraftAccountId] = useState(accountId);
  const [draftStartDate, setDraftStartDate] = useState(startDate || defaults.start);
  const [draftEndDate, setDraftEndDate] = useState(endDate || defaults.end);

  // Re-sync drafts when URL changes externally (browser back, deep-link).
  // When URL has no date, fall back to the same defaults (so Clear keeps the
  // useful last-30-days range visible).
  useEffect(() => {
    setDraftAccountId(accountId);
  }, [accountId]);
  useEffect(() => {
    setDraftStartDate(startDate || defaults.start);
  }, [startDate, defaults.start]);
  useEffect(() => {
    setDraftEndDate(endDate || defaults.end);
  }, [endDate, defaults.end]);

  // Server-side data
  const [accounts, setAccounts] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [entries, setEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // UI state
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [exporting, setExporting] = useState(false);

  // Race-condition guard: only the latest in-flight fetch updates state.
  const requestIdRef = useRef(0);

  // Ref to the scoped root, so we can measure the sticky page-top region
  // and feed its live height into the --lgrpg-top-h CSS variable. The
  // table thead reads that variable for its sticky offset.
  const rootRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const topEl = root.querySelector(".lg-page-top");
    if (!topEl) return;
    const measure = () => {
      const h = topEl.getBoundingClientRect().height;
      root.style.setProperty("--lgrpg-top-h", `${Math.round(h)}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(topEl);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  });

  // Load the account dropdown once.
  useEffect(() => {
    let cancelled = false;
    API.get("/accounts?limit=100000000000000")
      .then((res) => {
        if (!cancelled) setAccounts(res.data.accounts || []);
      })
      .catch((error) => {
        console.error("Error loading accounts:", error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the user's currency preference once.
  useEffect(() => {
    let cancelled = false;
    API.get("/users/me")
      .then((res) => {
        if (!cancelled) setCurrency(res.data.currency || "USD");
      })
      .catch((error) => {
        console.error("Error fetching currency:", error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the report whenever committed URL params change.
  useEffect(() => {
    if (!accountId) {
      setEntries([]);
      setOpeningBalance(0);
      setClosingBalance(0);
      setTotalCount(0);
      setErrorMessage("");
      setLoading(false);
      return;
    }

    const myRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage("");

    const params = { page, limit, accountId };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    API.get("/detailed-report", { params })
      .then((res) => {
        if (myRequestId !== requestIdRef.current) return;
        setOpeningBalance(res.data.openingBalance || 0);
        setClosingBalance(res.data.closingBalance || 0);
        setEntries(res.data.entries || []);
        setTotalCount(res.data.totalCount || 0);
      })
      .catch((error) => {
        if (myRequestId !== requestIdRef.current) return;
        console.error("fetchReport Error:", error);
        setErrorMessage(error.response?.data?.message || "Server didn't respond. Please try again.");
      })
      .finally(() => {
        if (myRequestId === requestIdRef.current) setLoading(false);
      });
  }, [page, limit, accountId, startDate, endDate]);

  // URL writer — omits defaults so the URL stays clean.
  const updateUrl = useCallback(
    (updates) => {
      const next = { ...parseDetailedReportParams(searchParams), ...updates };
      const params = new URLSearchParams();
      if (next.accountId) params.set("accountId", next.accountId);
      if (next.startDate) params.set("startDate", next.startDate);
      if (next.endDate) params.set("endDate", next.endDate);
      if (next.page > 1) params.set("page", String(next.page));
      if (next.limit !== DETAILED_REPORT_DEFAULT_LIMIT)
        params.set("limit", String(next.limit));
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = () => {
    if (!draftAccountId) return;
    updateUrl({
      accountId: draftAccountId,
      startDate: draftStartDate,
      endDate: draftEndDate,
      page: 1,
    });
  };

  const handleClearFilters = () => {
    setDraftAccountId("");
    // Reset draft dates to defaults so the UI keeps showing a sensible range.
    setDraftStartDate(defaults.start);
    setDraftEndDate(defaults.end);
    setSearchParams(new URLSearchParams());
  };

  // ────────────────────────────────────────────────────────────────
  // CSV export — DO NOT MODIFY. Byte-for-byte identical to the previous
  // implementation. The wrapping setExporting() calls are visual feedback
  // only and do not change what gets generated or downloaded.
  // ────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { accountId, startDate, endDate };
      const token = localStorage.getItem("token");
      const response = await API.get("/detailed-report/export", {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const csvBlob = new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "detailed-report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Export CSV Error:", error);
      setErrorMessage("Error exporting CSV. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (newPage) => updateUrl({ page: newPage });
  const handleRowsPerPageChange = (newLimit) =>
    updateUrl({ limit: parseInt(newLimit, 10), page: 1 });

  // Account object for display (resolved from the loaded accounts list).
  const draftAccount = useMemo(
    () => accounts.find((a) => a._id === draftAccountId) || null,
    [accounts, draftAccountId]
  );
  const committedAccount = useMemo(
    () => accounts.find((a) => a._id === accountId) || null,
    [accounts, accountId]
  );

  const hasFilters = !!(draftAccountId || draftStartDate || draftEndDate);

  // Derived view mode: prefetch / loading / error / empty / loaded
  const mode = (() => {
    if (!accountId) return "prefetch";
    if (loading) return "loading";
    if (errorMessage) return "error";
    // entries.length 0 means server returned no transactions (only opening row possibly).
    // The server-returned `entries` always includes the opening row, so we check if
    // there are any non-opening rows.
    const nonOpeningCount = entries.filter((e) => !e.isOpeningRow).length;
    if (nonOpeningCount === 0) return "empty";
    return "loaded";
  })();

  // Transaction count for the summary — exclude the synthetic opening row.
  const transactionCount = Math.max(0, totalCount - 1);

  return (
    <>
      <style>{PAGE_CSS}</style>
      <div className="lgrpg-root" ref={rootRef}>
        <div className="lg-page">
          <div className="lg-page-top">
            <header className="lg-header">
              <div className="lg-header-l">
                <div className="lg-header-title-wrap">
                  <h1>Ledger</h1>
                  <p className="lg-sub">Account transaction history with running balance</p>
                </div>
              </div>
            </header>
            <div className="lg-main-top">
              <FilterBar
                draftAccount={draftAccount}
                setDraftAccount={(acc) => setDraftAccountId(acc?._id || "")}
                accounts={accounts}
                draftStart={draftStartDate}
                setDraftStart={setDraftStartDate}
                draftEnd={draftEndDate}
                setDraftEnd={setDraftEndDate}
                onSearch={handleSearch}
                onExport={handleExport}
                onClear={handleClearFilters}
                hasFilters={hasFilters}
                exporting={exporting}
                busy={loading}
              />
              {(mode === "loaded" || mode === "empty") && committedAccount && (
                <SummaryBand
                  account={committedAccount}
                  start={startDate}
                  end={endDate}
                  opening={openingBalance}
                  closing={closingBalance}
                  txCount={transactionCount}
                  currency={currency}
                />
              )}
            </div>
          </div>

          <main className="lg-main">
            {mode === "prefetch" && <PreFetchState />}
            {mode === "loading" && <LoadingState />}
            {mode === "error" && (
              <ErrorState
                message={errorMessage}
                onRetry={() => {
                  // Trigger a refetch by bumping the URL (re-set same params).
                  updateUrl({});
                }}
              />
            )}
            {mode === "empty" && <EmptyState />}
            {mode === "loaded" && (
              <>
                <LedgerTable
                  entries={entries}
                  opening={openingBalance}
                  closing={closingBalance}
                  startIso={startDate}
                  endIso={endDate}
                  currency={currency}
                />
                <Pagination
                  page={page}
                  perPage={limit}
                  total={totalCount}
                  onPage={handlePageChange}
                  onPer={handleRowsPerPageChange}
                />
              </>
            )}
          </main>
        </div>
      </div>
    </>
  );
}

export default DetailedReportPage;
